import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Moon, MessageSquarePlus, Check, ChevronDown } from "lucide-react";
import { apiInstance as api } from "../../../hooks/useApi";
import { useDashTheme } from "../hooks/useDashTheme";
import { Pagination, EmptyState } from "./DataTable";
import { Shimmer } from "./StatCard";

const FILTERS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "7 days" },
  { key: "last14", label: "14 days" },
  { key: "month", label: "Month" },
];

const fmtSec = (sec) => {
  if (!sec || sec <= 0) return "0m";
  const min = Math.floor(sec / 60);
  if (min <= 0) return "<1m";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + "m" : ""}`.trim() : `${m}m`;
};

const fmtMs = (ms) => {
  if (!ms || ms <= 0) return "0m";
  const min = Math.floor(ms / 60000);
  if (min <= 0) return "<1m";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + "m" : ""}`.trim() : `${m}m`;
};

const fmtTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// Date only — for rows whose timestamps are a query window, not a real span.
const fmtDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" }) : "—";

// Clock time only (no date) — used for the second half of a same-day range.
const fmtClock = (iso) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";

// A report's `timestamp` is the END of its agent interval, and `totalSec` is the
// interval length (verified: consecutive reports have gap === total_sec). So the
// window is [timestamp - totalSec, timestamp]. Normal intervals are ~3m, where a
// range adds nothing; only long intervals (sleep/wake catch-up) are worth showing.
const LONG_INTERVAL_SEC = 600; // 10m
const intervalWindow = (log) => {
  const endIso = log.createdAt || log.timestamp;
  const total = Number(log.totalSec) || 0;
  if (!endIso || total < LONG_INTERVAL_SEC) return null;
  const end = new Date(endIso);
  const start = new Date(end.getTime() - total * 1000);
  if (Number.isNaN(start.getTime())) return null;
  // Same calendar day → "2 Aug, 16:41 → 17:21"; else show the date on both ends.
  const sameDay = start.toDateString() === end.toDateString();
  return sameDay
    ? `${fmtTime(start.toISOString())} → ${fmtClock(end.toISOString())}`
    : `${fmtTime(start.toISOString())} → ${fmtTime(end.toISOString())}`;
};

// Gap-fill rows aggregate whole days, and a multi-day filter (7/14/month) folds
// several days into ONE row. Label it by the day count parsed from idle_note so
// a 6-day total never reads as a single day.
const gapFillLabel = (log) => {
  const days = Number(String(log?.idle_note || "").match(/\((\d+) day/)?.[1]) || 0;
  if (days > 1) return `${fmtDay(log.startTime)} – ${fmtDay(log.endTime)} · ${days} days total`;
  return `${fmtDay(log.startTime)} · full day total`;
};

/**
 * Opt-in stealth-agent idle intervals + employee reasons.
 * Also loads tracked session idle (same source as Team performance Idle chip)
 * so admins see the real idle breakdown + notes, not only agent report noise.
 */
export function StealthIdleReasonModal({ isOpen, onClose, userId, memberName, onFallbackToRegular }) {
  const t = useDashTheme();
  const [filter, setFilter] = React.useState("today");
  const [page, setPage] = React.useState(1);
  const [scopeAll, setScopeAll] = React.useState(false);
  const [logs, setLogs] = React.useState([]);
  const [sessionLogs, setSessionLogs] = React.useState([]);
  const [meta, setMeta] = React.useState({
    total: 0,
    totalPages: 1,
    canWrite: false,
    canViewAll: false,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const [draft, setDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const abortRef = React.useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setFilter("today");
    setPage(1);
    setScopeAll(false);
    setLogs([]);
    setSessionLogs([]);
    setEditingId(null);
    setDraft("");
  }, [isOpen, userId]);

  React.useEffect(() => {
    setPage(1);
  }, [filter, scopeAll]);

  React.useEffect(() => {
    if (!isOpen) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = new URLSearchParams({ filter, page: String(page) });
        if (scopeAll) params.append("scope", "all");
        else if (userId) params.append("userId", userId);

        const stealthReq = api.get(`/api/v1/stealth-agent/idle-reasons?${params.toString()}`, {
          signal: controller.signal,
        });

        // Session idle matches Team performance Idle chip (TrackingSession).
        const sessionReq =
          !scopeAll && userId
            ? api.get(
                `/api/v2/tracking/idle-logs?${new URLSearchParams({
                  filter,
                  page: "1",
                  userId: String(userId),
                }).toString()}`,
                { signal: controller.signal }
              )
            : Promise.resolve(null);

        const [stealthRes, sessionRes] = await Promise.all([stealthReq, sessionReq]);
        if (controller.signal.aborted) return;

        if (stealthRes.data?.success || stealthRes.data?.ok) {
          const d = stealthRes.data.data || {};
          setLogs(d.logs || []);
          setMeta({
            total: d.total || 0,
            totalPages: d.totalPages || 1,
            canWrite: d.canWrite === true,
            canViewAll: d.canViewAll === true,
          });
        } else {
          setError("Failed to load idle intervals.");
        }

        if (sessionRes?.data?.success) {
          setSessionLogs(sessionRes.data.data?.logs || []);
        } else {
          setSessionLogs([]);
        }
      } catch (err) {
        if (err.name === "AbortError" || err.name === "CanceledError") return;
        const code = err.response?.data?.code;
        if (
          !scopeAll &&
          (code === "NOT_DESKTOP_AGENT" || code === "FEATURE_DISABLED") &&
          typeof onFallbackToRegular === "function"
        ) {
          onFallbackToRegular();
          return;
        }
        setError(err.response?.data?.error || "Failed to load idle intervals.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [isOpen, filter, page, userId, scopeAll, onFallbackToRegular]);

  const sessionTotalMs = sessionLogs.reduce((s, l) => s + (l.idleTime || 0), 0);
  const reasonTotalSec = logs.reduce((s, l) => s + (l.idleSec || 0), 0);
  const viewOnly = !meta.canWrite;
  const hasSessions = sessionLogs.length > 0;
  // Agent report idle ≠ Team performance Idle chip. For admins, only surface
  // intervals that already have a written reason (avoid "4h vs 1h" confusion).
  const reasonLogs = viewOnly ? logs.filter((l) => Boolean(l.reason)) : logs;
  const hasReasons = reasonLogs.length > 0;
  const showReasonSection = meta.canWrite || hasReasons;

  const startEdit = (log) => {
    if (!meta.canWrite) return;
    setEditingId(log.reportId);
    setDraft(log.reason || "");
  };

  const saveReason = async (reportId) => {
    if (!meta.canWrite) return;
    const reason = draft.trim();
    if (!reason) return;
    setSaving(true);
    try {
      const res = await api.put(`/api/v1/stealth-agent/idle-reasons/${reportId}`, { reason });
      if (res.data?.success || res.data?.ok) {
        const updated = res.data.data;
        setLogs((prev) =>
          prev.map((l) =>
            l.reportId === reportId
              ? { ...l, reason: updated.reason, reasonUpdatedAt: updated.reasonUpdatedAt }
              : l
          )
        );
        setEditingId(null);
        setDraft("");
      } else {
        setError("Could not save reason.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Could not save reason.");
    } finally {
      setSaving(false);
    }
  };

  const headerIdleLabel = hasSessions
    ? `${fmtMs(sessionTotalMs)} tracked idle`
    : `${fmtSec(reasonTotalSec)} agent idle`;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(8,10,18,0.55)",
              backdropFilter: "blur(4px)",
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={t.motion.spring.snappy}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 640,
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              background: t.c.surface,
              border: `1px solid ${t.c.border}`,
              borderRadius: t.r.lg,
              boxShadow: t.shadow.lg,
              overflow: "hidden",
              fontFamily: t.font.family,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
                padding: `${t.s[5]}px ${t.s[5]}px ${t.s[4]}px`,
                borderBottom: `1px solid ${t.c.border}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: t.status.warningSoft,
                    color: t.status.warning,
                  }}
                >
                  <Moon size={18} />
                </span>
                <div>
                  <div style={{ fontSize: t.font.size.lg, fontWeight: 600, color: t.c.text }}>
                    Idle time
                  </div>
                  <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                    {scopeAll ? "All desktop-agent users" : (memberName || "Team member")}
                    {" · "}{headerIdleLabel}
                    {hasSessions ? ` · ${sessionLogs.length} sessions` : meta.total ? ` · ${meta.total} intervals` : ""}
                    {meta.canWrite ? " · add a reason on an interval below" : " · view only"}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  width: 32,
                  height: 32,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  border: "none",
                  background: t.c.surfaceMuted,
                  color: t.c.textSecondary,
                  cursor: "pointer",
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div
              style={{
                display: "flex",
                gap: 6,
                padding: `${t.s[3]}px ${t.s[5]}px`,
                borderBottom: `1px solid ${t.c.border}`,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    style={{
                      height: 30,
                      padding: "0 12px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1px solid ${active ? "transparent" : t.c.border}`,
                      background: active ? t.c.primary : "transparent",
                      color: active ? "#fff" : t.c.textSecondary,
                      fontSize: t.font.size.sm,
                      fontWeight: 500,
                      fontFamily: t.font.family,
                      transition: "all 140ms",
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
              {meta.canViewAll && (
                <>
                  <span style={{ width: 1, height: 20, background: t.c.border, margin: "0 4px" }} />
                  <button
                    type="button"
                    onClick={() => setScopeAll(false)}
                    style={{
                      height: 30,
                      padding: "0 12px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1px solid ${!scopeAll ? "transparent" : t.c.border}`,
                      background: !scopeAll ? t.c.primary : "transparent",
                      color: !scopeAll ? "#fff" : t.c.textSecondary,
                      fontSize: t.font.size.sm,
                      fontWeight: 500,
                      fontFamily: t.font.family,
                    }}
                  >
                    This member
                  </button>
                  <button
                    type="button"
                    onClick={() => setScopeAll(true)}
                    style={{
                      height: 30,
                      padding: "0 12px",
                      borderRadius: 99,
                      cursor: "pointer",
                      border: `1px solid ${scopeAll ? "transparent" : t.c.border}`,
                      background: scopeAll ? t.c.primary : "transparent",
                      color: scopeAll ? "#fff" : t.c.textSecondary,
                      fontSize: t.font.size.sm,
                      fontWeight: 500,
                      fontFamily: t.font.family,
                    }}
                  >
                    All members
                  </button>
                </>
              )}
            </div>

            <div
              style={{
                overflowY: "auto",
                padding: t.s[4],
                display: "flex",
                flexDirection: "column",
                gap: t.s[3],
                flex: 1,
              }}
            >
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      padding: t.s[3],
                      borderRadius: t.r.md,
                      border: `1px solid ${t.c.border}`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    <Shimmer w="40%" h={12} />
                    <Shimmer w="70%" h={10} />
                  </div>
                ))
              ) : error ? (
                <EmptyState icon={<Clock size={22} />} title="Couldn’t load idle time" description={error} />
              ) : !hasSessions && !showReasonSection ? (
                <EmptyState
                  icon={<Moon size={22} />}
                  title="No idle session details"
                  description="Team performance Idle can include live/screenshot idle that isn’t stored as a session note yet. No employee reason has been added for this range."
                />
              ) : (
                <>
                  {hasSessions && (
                    <section style={{ display: "flex", flexDirection: "column", gap: t.s[2] }}>
                      <SectionLabel
                        t={t}
                        title="Tracked idle"
                        subtitle="Same source as Team performance Idle"
                      />
                      {sessionLogs.map((log, i) => (
                        <SessionIdleRow key={log._id || i} log={log} t={t} index={i} />
                      ))}
                    </section>
                  )}

                  {showReasonSection && (
                    <section style={{ display: "flex", flexDirection: "column", gap: t.s[2] }}>
                      <SectionLabel
                        t={t}
                        title="Reason intervals"
                        subtitle={
                          meta.canWrite
                            ? "Desktop Agent windows (≥1m) — add why you were idle. This is not the Team Idle total."
                            : "Employee-written reasons on Desktop Agent windows (not the Team Idle total)."
                        }
                      />
                      {!hasReasons ? (
                        <EmptyState
                          icon={<Moon size={22} />}
                          title="No reason intervals"
                          description="No desktop-agent idle windows of 1m+ in this range yet."
                        />
                      ) : (
                        reasonLogs.map((log, i) => {
                          const isEditing = editingId === log.reportId;
                          return (
                            <div
                              key={log.reportId || i}
                              style={{
                                borderRadius: t.r.md,
                                border: `1px solid ${isEditing ? t.c.primary : t.c.border}`,
                                background: t.c.surfaceMuted,
                                padding: `${t.s[3]}px ${t.s[4]}px`,
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 30,
                                    height: 30,
                                    borderRadius: 8,
                                    background: t.status.warningSoft,
                                    color: t.status.warning,
                                    flexShrink: 0,
                                  }}
                                >
                                  <Clock size={15} />
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {scopeAll && log.memberName && (
                                    <div
                                      style={{
                                        fontSize: t.font.size.sm,
                                        fontWeight: 600,
                                        color: t.c.text,
                                        marginBottom: 2,
                                      }}
                                    >
                                      {log.memberName}
                                    </div>
                                  )}
                                  <div style={{ fontSize: t.font.size.md, color: t.c.text, fontWeight: 500 }}>
                                    {intervalWindow(log) || fmtTime(log.createdAt || log.timestamp)}
                                  </div>
                                  {(log.activeApp || log.windowTitle) && (
                                    <div
                                      style={{
                                        fontSize: t.font.size.xs,
                                        color: t.c.textTertiary,
                                        marginTop: 2,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {[log.activeApp, log.windowTitle].filter(Boolean).join(" · ")}
                                    </div>
                                  )}
                                  {!isEditing && (
                                    <div
                                      style={{
                                        fontSize: t.font.size.sm,
                                        color: log.reason ? t.c.textSecondary : t.c.textTertiary,
                                        marginTop: 4,
                                        fontStyle: log.reason ? "normal" : "italic",
                                      }}
                                    >
                                      {log.reason
                                        ? `Reason: ${log.reason}`
                                        : viewOnly
                                          ? "No reason yet"
                                          : "No reason yet — add one"}
                                    </div>
                                  )}
                                </div>
                                <span
                                  style={{
                                    fontSize: t.font.size.md,
                                    fontWeight: 600,
                                    color: t.status.warning,
                                    fontVariantNumeric: "tabular-nums",
                                    flexShrink: 0,
                                  }}
                                >
                                  {fmtSec(log.idleSec)}
                                </span>
                                {meta.canWrite && !isEditing && (
                                  <button
                                    type="button"
                                    onClick={() => startEdit(log)}
                                    title={log.reason ? "Edit reason" : "Add reason"}
                                    style={{
                                      height: 32,
                                      padding: "0 10px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                      borderRadius: 8,
                                      border: `1px solid ${t.c.primary}`,
                                      background: t.c.primary,
                                      color: "#fff",
                                      cursor: "pointer",
                                      flexShrink: 0,
                                      fontSize: t.font.size.sm,
                                      fontWeight: 600,
                                      fontFamily: t.font.family,
                                    }}
                                  >
                                    <MessageSquarePlus size={14} />
                                    {log.reason ? "Edit" : "Add reason"}
                                  </button>
                                )}
                              </div>

                              {isEditing && (
                                <div style={{ marginTop: 12 }}>
                                  <textarea
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
                                    onKeyDown={(e) => {
                                      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                        e.preventDefault();
                                        if (draft.trim() && !saving) saveReason(log.reportId);
                                      }
                                      if (e.key === "Escape") {
                                        setEditingId(null);
                                        setDraft("");
                                      }
                                    }}
                                    placeholder="Why were you idle during this interval?"
                                    rows={3}
                                    autoFocus
                                    style={{
                                      width: "100%",
                                      boxSizing: "border-box",
                                      resize: "vertical",
                                      minHeight: 72,
                                      borderRadius: t.r.md,
                                      border: `1px solid ${t.c.border}`,
                                      background: t.c.surface,
                                      color: t.c.text,
                                      padding: 10,
                                      fontSize: t.font.size.sm,
                                      fontFamily: t.font.family,
                                      display: "block",
                                    }}
                                  />
                                  <div
                                    style={{
                                      marginTop: 10,
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 8,
                                      justifyContent: "flex-end",
                                      alignItems: "center",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingId(null);
                                        setDraft("");
                                      }}
                                      disabled={saving}
                                      style={{
                                        height: 34,
                                        padding: "0 14px",
                                        borderRadius: 8,
                                        border: `1px solid ${t.c.border}`,
                                        background: t.c.surface,
                                        color: t.c.text,
                                        cursor: "pointer",
                                        fontSize: t.font.size.sm,
                                        fontWeight: 500,
                                        fontFamily: t.font.family,
                                      }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => saveReason(log.reportId)}
                                      disabled={saving || !draft.trim()}
                                      style={{
                                        height: 34,
                                        padding: "0 14px",
                                        borderRadius: 8,
                                        border: "none",
                                        background: t.c.primary || "#5b5fc7",
                                        color: "#fff",
                                        cursor: saving || !draft.trim() ? "not-allowed" : "pointer",
                                        fontSize: t.font.size.sm,
                                        fontWeight: 600,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 6,
                                        opacity: saving || !draft.trim() ? 0.6 : 1,
                                        fontFamily: t.font.family,
                                      }}
                                    >
                                      <Check size={14} />
                                      {saving ? "Saving…" : "Save reason"}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </section>
                  )}
                </>
              )}
            </div>

            {meta.totalPages > 1 && (
              <Pagination
                page={page}
                pages={meta.totalPages}
                total={meta.total}
                pageLabel="intervals"
                onChange={setPage}
              />
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function SectionLabel({ t, title, subtitle }) {
  return (
    <div style={{ marginTop: 2 }}>
      <div style={{ fontSize: t.font.size.sm, fontWeight: 600, color: t.c.text }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2 }}>{subtitle}</div>
      )}
    </div>
  );
}

function SessionIdleRow({ log, t, index }) {
  const [open, setOpen] = React.useState(false);
  const events = log.idleEvents || [];
  const multi = events.length > 1;
  const note =
    events.length === 1
      ? events[0].idle_note || log.idle_note || null
      : multi
        ? null
        : log.idle_note || null;
  const sourceHint =
    log.source === "screenshot_idle"
      ? "From live activity (same as Team Idle)"
      : log.source === "stealth_gapfill"
        ? "Desktop Agent (gap-fill day)"
        : null;
  // Gap-fill rows carry the QUERY WINDOW in startTime/endTime, not a real idle
  // span (see tracking_v2 idle-logs: startTime = startDate). Rendering it as a
  // clock range reads as "idle began 00:00", which is false — show the day
  // instead, so the number stays a daily total.
  const isGapFill = log.source === "stealth_gapfill";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.2) }}
      style={{
        borderRadius: t.r.md,
        border: `1px solid ${t.c.border}`,
        overflow: "hidden",
        background: t.c.surfaceMuted,
      }}
    >
      <div
        onClick={multi ? () => setOpen((v) => !v) : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: `${t.s[3]}px ${t.s[4]}px`,
          cursor: multi ? "pointer" : "default",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 30,
            borderRadius: 8,
            background: t.status.warningSoft,
            color: t.status.warning,
            flexShrink: 0,
          }}
        >
          <Clock size={15} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: t.font.size.md, color: t.c.text, fontWeight: 500 }}>
            {isGapFill ? (
              <span style={{ color: t.c.textTertiary, fontWeight: 400 }}>{gapFillLabel(log)}</span>
            ) : (
              <>
                {fmtTime(log.startTime)}{" "}
                <span style={{ color: t.c.textTertiary, fontWeight: 400 }}>→ {fmtTime(log.endTime)}</span>
              </>
            )}
          </div>
          {(note || sourceHint) && (
            <div
              style={{
                fontSize: t.font.size.sm,
                color: note ? t.c.textSecondary : t.c.textTertiary,
                marginTop: 2,
                fontStyle: note ? "normal" : "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {note ? `Note: ${note}` : sourceHint}
            </div>
          )}
          {multi && (
            <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 2 }}>
              {events.length} idle events
            </div>
          )}
        </div>
        <span
          style={{
            fontSize: t.font.size.md,
            fontWeight: 600,
            color: t.status.warning,
            fontVariantNumeric: "tabular-nums",
            flexShrink: 0,
          }}
        >
          {fmtMs(log.idleTime)}
        </span>
        {multi && (
          <ChevronDown
            size={16}
            style={{
              color: t.c.textTertiary,
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 160ms",
              flexShrink: 0,
            }}
          />
        )}
      </div>

      <AnimatePresence>
        {open && multi && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ borderTop: `1px solid ${t.c.border}`, background: t.c.surface }}
          >
            {events.map((ev, j) => (
              <div
                key={j}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: `${t.s[2]}px ${t.s[4]}px`,
                  borderBottom: j < events.length - 1 ? `1px solid ${t.c.border}` : "none",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>
                    {fmtTime(ev.idleStartTime)} → {fmtTime(ev.idleEndTime)}
                  </div>
                  {ev.idle_note && (
                    <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 1 }}>
                      {ev.idle_note}
                    </div>
                  )}
                </div>
                <span
                  style={{
                    fontSize: t.font.size.sm,
                    fontWeight: 600,
                    color: t.c.textSecondary,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtMs(ev.idleTimeMs)}
                </span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
